import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AIService } from 'src/app/ai/ai.service';
import LinkedEmployee from 'src/app/models/LinkedEmployee';
import { UpdateDefaultEmployeeContract } from '../project.actions';

@Component({
    selector: 'app-edit-default-linked-employee-modal',
    templateUrl: './edit-default-linked-employee-modal.component.html',
    styleUrls: ['./edit-default-linked-employee-modal.component.scss'],
})
export class EditDefaultLinkedEmployeeModalComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();
    @Input() linkedEmployee: LinkedEmployee;
    editForm: FormGroup;
    contractFileName: string = '';
    aiExtracting = false;

    constructor(
        public activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private store: Store,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private aiService: AIService,
    ) {}

    ngOnInit(): void {
        this.editForm = this.fb.group({
            pricePerHour: [this.linkedEmployee.pricePerHour, Validators.required],
            budget: [this.linkedEmployee.budget ?? null],
            effectiveFrom: [this.linkedEmployee.effectiveFrom, Validators.required],
            effectiveTo: [this.linkedEmployee.effectiveTo || null],
            contractFile: [null],
            poNumber: [this.linkedEmployee.poNumber || ''],
            sesNumber: [this.linkedEmployee.sesNumber || ''],
            contractAgreementNumber: [this.linkedEmployee.contractAgreementNumber || ''],
            freeText: [this.linkedEmployee.freeText || ''],
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private readFileAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const res = (reader.result as string) || '';
                const base64 = res.includes('base64,') ? res.split('base64,')[1] : res;
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async extractInvoiceFieldsWithAi(): Promise<void> {
        try {
            this.aiExtracting = true;
            const contractFileValue: any = this.editForm.value?.contractFile || this.linkedEmployee?.clientContractUrl || null;
            if (!contractFileValue) {
                this.toastr.info('Please upload/select a contract document first.');
                return;
            }

            let source: string;
            if (typeof contractFileValue === 'string') {
                source = contractFileValue;
            } else if (contractFileValue instanceof File) {
                const base64 = await this.readFileAsBase64(contractFileValue);
                const mime = contractFileValue.type || 'application/pdf';
                source = `data:${mime};base64,${base64}`;
            } else {
                this.toastr.error('Unsupported contract file source for AI extraction.');
                return;
            }

            const result = await this.aiService.suggestInvoiceContractFieldsFromPdfUrl(source);
            this.editForm.patchValue({
                pricePerHour: result.pricePerHour ?? this.editForm.value?.pricePerHour ?? '',
                budget: result.budget ?? this.editForm.value?.budget ?? null,
                effectiveFrom: result.effectiveFrom || this.editForm.value?.effectiveFrom || '',
                effectiveTo: result.effectiveTo || this.editForm.value?.effectiveTo || '',
                poNumber: result.poNumber || this.editForm.value?.poNumber || '',
                sesNumber: result.sesNumber || this.editForm.value?.sesNumber || '',
                contractAgreementNumber: result.contractAgreementNumber || this.editForm.value?.contractAgreementNumber || '',
                freeText: result.freeText || this.editForm.value?.freeText || '',
            });

            if (!result.pricePerHour && !result.budget && !result.effectiveFrom && !result.effectiveTo && !result.poNumber && !result.sesNumber && !result.contractAgreementNumber && !result.freeText) {
                this.toastr.info('AI found no invoice reference fields in this document.');
            } else {
                this.toastr.success('AI fields extracted and prefilled.');
            }
        } catch (err: any) {
            this.logger.error('AI extraction for default employee link failed', err);
            this.toastr.error(err?.message || 'AI extraction failed.');
        } finally {
            this.aiExtracting = false;
        }
    }

    async save() {
        const contractFileValue: any = this.editForm.value?.contractFile || null;
        let payload: any = {
            uuid: this.linkedEmployee.uuid,
            pricePerHour: this.editForm.value.pricePerHour,
            budget: this.editForm.value.budget !== null && this.editForm.value.budget !== '' ? Number(this.editForm.value.budget) : null,
            effectiveFrom: this.editForm.value.effectiveFrom,
            effectiveTo: this.editForm.value.effectiveTo,
            poNumber: this.editForm.value.poNumber,
            sesNumber: this.editForm.value.sesNumber,
            contractAgreementNumber: this.editForm.value.contractAgreementNumber,
            freeText: this.editForm.value.freeText,
        };

        if (contractFileValue) {
            if (typeof contractFileValue === 'string') {
                payload.contractUrl = contractFileValue;
                try {
                    const parts = contractFileValue.split('/');
                    payload.contractFileName = parts[parts.length - 1];
                } catch (e) {}
            } else if (contractFileValue instanceof File) {
                const base64 = await this.readFileAsBase64(contractFileValue);
                payload.contractBase64 = base64;
                payload.contractFileName = contractFileValue.name;
                payload.contractMimeType = contractFileValue.type || 'application/octet-stream';
            }
        }

        this.subscriptions.add(
            this.store.dispatch(new UpdateDefaultEmployeeContract(payload)).subscribe(
                () => {
                    this.toastr.success('Employee link updated');
                    this.activeModal.close();
                },
                () => {
                    this.logger.error('Failed to update employee link');
                    this.toastr.error('Update failed');
                },
            ),
        );
    }
}
